import {
  ActionError,
  AttentionBlock,
  Button,
  ChoiceRow,
  FieldGrid,
  FormBlock,
  FormFooter,
  PageHeader,
  PageTitle,
  Radio,
  RadioGroup,
  ReadingColumn,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { Page } from '@/app/shell'
import { TextField } from '@/features/clinic-profile/blocks'
import { emailError, personNameError, placeFieldErrors } from '@/features/clinic-profile/form'
import {
  focusFirstInvalid,
  useFormState,
  type FormErrors,
} from '@/features/clinic-profile/use-form-state'
import type { AccessMode } from '@/features/users/api'
import { TemporaryPassword } from '@/features/users/temporary-password'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import { useAddTeamMember, type AddedTeamMember } from './api'
import { isRootEmailAllowed, ROOT_EMAIL_REFUSAL } from './root-email'

/**
 * Adicionar à equipe da Westhetics (docs/areas/administracao.md, "Adicionar à equipe"; RN-AUT-28).
 * Arquétipo: cadastro. Como criar usuário, sem perfil nem clínica: quem entra na equipe é root. O
 * e-mail que já tem conta (uma gestora, por exemplo) só ganha o acesso e mantém as clínicas que tem.
 */

interface NewTeamMemberState {
  name: string
  email: string
  accessMode: AccessMode
}

const EMPTY: NewTeamMemberState = { name: '', email: '', accessMode: 'invite' }

const KEYS = ['name', 'email'] as const

// As mensagens são as da API, para o erro dizer o mesmo dos dois lados. Só e-mail da empresa entra
// na equipe (RN-AUT-31): o domínio é conferido depois do formato.
function validate(values: NewTeamMemberState): FormErrors<NewTeamMemberState> {
  return { name: personNameError(values.name), email: teamEmailError(values.email) }
}

function teamEmailError(email: string) {
  return emailError(email) ?? (isRootEmailAllowed(email) ? undefined : ROOT_EMAIL_REFUSAL)
}

const FIELD_KEYS: Record<string, keyof NewTeamMemberState> = {
  name: 'name',
  email: 'email',
  accessMode: 'accessMode',
}

const fieldKey = (path: string) => FIELD_KEYS[path]

export function NewTeamMemberPage() {
  const session = useSession()
  const navigate = useNavigate()
  const add = useAddTeamMember()
  const form = useFormState<NewTeamMemberState>(() => EMPTY, validate)
  const formRef = useRef<HTMLFormElement>(null)
  // Com senha temporária, a tela fica para mostrar a senha uma única vez.
  const [added, setAdded] = useState<AddedTeamMember | null>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.check(KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    const values = form.values
    add.mutate(
      {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        accessMode: values.accessMode,
      },
      {
        onSuccess: (result) => {
          const { access, member } = result
          const name = titleCase(member.name)
          if (access.mode === 'temporary_password') {
            setAdded(result)
            return
          }
          if (access.mode === 'existing_account') {
            toast(
              access.emailSent
                ? `${name} já tinha conta; agora é da equipe da Westhetics`
                : `${name} já tinha conta; agora é da equipe da Westhetics — o e-mail de aviso não saiu`,
            )
            navigate(routes.admin.team)
            return
          }
          if (access.emailSent) {
            toast(`Convite enviado a ${member.email} — vale 7 dias`)
            navigate(routes.admin.team)
            return
          }
          // A pessoa está na equipe, mas o convite não chegou: a saída é reenviar, na ficha dela.
          toast('A pessoa entrou na equipe, mas o e-mail não saiu. Reenvie o convite.')
          navigate(routes.admin.teamMember(member.id))
        },
        onError: (error) => {
          form.refuse(placeFieldErrors<NewTeamMemberState>(error, fieldKey).fields)
          focusFirstInvalid(formRef.current)
        },
      },
    )
  }

  if (added) return <AddedWithTemporaryPassword added={added} />

  const failure = add.isError ? placeFieldErrors<NewTeamMemberState>(add.error, fieldKey) : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)
  // Quem adiciona com e-mail de fora da empresa sai da equipe quando a primeira pessoa da empresa
  // entrar (RN-AUT-31): a tela diz antes, porque a sessão acaba na hora.
  const ownEmail = session.profile?.email ?? ''
  const selfOutside = ownEmail !== '' && !isRootEmailAllowed(ownEmail)
  const existing =
    add.error && isApiError(add.error) && add.error.code === 'conflict'
      ? (add.error.details.existing as { id: string } | undefined)
      : undefined

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Equipe', href: routes.admin.team }, { label: 'adicionar' }]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Adicionar à equipe"
            subtitle="Quem entra na equipe vê todas as clínicas e, ao entrar numa delas, age como gestora. Use só para quem trabalha na Westhetics."
          />

          <div className="mt-6 flex flex-col gap-4">
            {showFailure ? (
              <ActionError
                title="Não foi possível adicionar à equipe"
                description={
                  failure.unplaced.length > 0
                    ? failure.unplaced.join(' ')
                    : `${errorMessage(add.error)} Nada foi criado; o que você digitou continua na tela.`
                }
                actions={
                  existing ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={routes.admin.teamMember(existing.id)}>Abrir a pessoa</Link>
                    </Button>
                  ) : undefined
                }
              />
            ) : null}

            {selfOutside ? (
              <AttentionBlock title="Ao adicionar alguém com e-mail da empresa, você sai da equipe">
                O seu e-mail, {ownEmail}, é de fora da empresa. Quando a primeira pessoa com e-mail
                @westhetics.io, @westhetics.com.br ou @bytewell.co entrar, a sua conta e as outras
                de fora saem da equipe, e as sessões abertas acabam na hora. Para continuar,
                adicione o seu e-mail da empresa e entre com ele.
              </AttentionBlock>
            ) : null}

            <FormBlock
              title="Identificação"
              note="O nome aparece na trilha de ações de toda clínica em que a pessoa agir. O e-mail é o login e não muda depois: só entra na equipe e-mail @westhetics.io, @westhetics.com.br ou @bytewell.co."
            >
              <FieldGrid>
                <TextField form={form} name="name" label="Nome" maxLength={120} />
                <TextField
                  form={form}
                  name="email"
                  label="E-mail"
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  placeholder="nome@westhetics.io"
                />
              </FieldGrid>
            </FormBlock>

            <FormBlock
              title="Acesso"
              note="Como a pessoa entra pela primeira vez. Se o e-mail já tem conta na Westhetics, ela entra com a senha que já tem e continua nas clínicas em que está."
            >
              <RadioGroup
                aria-label="Acesso"
                className="tablet:grid-cols-2"
                value={form.values.accessMode}
                onValueChange={(value) => form.update({ accessMode: value as AccessMode })}
              >
                <ChoiceRow
                  control={<Radio value="invite" />}
                  description="A pessoa recebe um link para criar a senha, válido por 7 dias."
                >
                  Convite por e-mail
                </ChoiceRow>
                <ChoiceRow
                  control={<Radio value="temporary_password" />}
                  description="A senha aparece uma única vez nesta tela, para você entregar. A troca é obrigatória no primeiro acesso."
                >
                  Senha temporária
                </ChoiceRow>
              </RadioGroup>
              {form.errors.accessMode ? (
                <p
                  role="alert"
                  className="font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal"
                >
                  {form.errors.accessMode}
                </p>
              ) : null}
            </FormBlock>
            <FormFooter
              secondary={
                <Button variant="outline" asChild>
                  <Link to={routes.admin.team}>Cancelar</Link>
                </Button>
              }
              primary={
                <Button type="submit" loading={add.isPending} loadingLabel="Adicionando à equipe…">
                  Adicionar à equipe
                </Button>
              }
            />
          </div>
        </ReadingColumn>
      </Page>
    </form>
  )
}

/** A pessoa entrou com senha temporária: a senha aparece aqui, uma única vez, com "Copiar". */
function AddedWithTemporaryPassword({ added }: { added: AddedTeamMember }) {
  const { access, member } = added
  const password = access.mode === 'temporary_password' ? access.temporaryPassword : null
  const name = titleCase(member.name)

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Equipe', href: routes.admin.team }, { label: name }]}
      />
      <Page>
        <ReadingColumn>
          <PageTitle
            title="Pessoa adicionada à equipe"
            subtitle={`${name} entra na Westhetics com o acesso da equipe. Falta entregar a senha.`}
          />
          <TemporaryPassword
            className="mt-6"
            name={member.name}
            password={password}
            missing={
              <>
                A pessoa já tinha sido adicionada na primeira tentativa, e a senha só é mostrada uma
                vez. Para {member.email} entrar, abra a ficha e reenvie o convite.
              </>
            }
          >
            Esta senha não aparece de novo. Copie e entregue a {member.email}; a troca é obrigatória
            no primeiro acesso.
          </TemporaryPassword>
          {!access.emailSent ? (
            <p className="font-body text-secondary mt-3 text-[12px] leading-[1.6] font-light">
              O e-mail avisando {member.email} do acesso não saiu. Entregue a senha por outro canal.
            </p>
          ) : null}
          <div className="mt-6 flex flex-wrap gap-[10px]">
            <Button asChild>
              <Link to={routes.admin.team}>Voltar para a equipe</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to={routes.admin.teamMember(member.id)}>Abrir a ficha</Link>
            </Button>
          </div>
        </ReadingColumn>
      </Page>
    </>
  )
}
